"use client";

import React from "react";
import { MODAL_IDS, useGlobalModalStack } from "@wtc/ui";
import { UnifiedModal } from "@/components/shared/modals";
import { AssignToUserSelect } from "@/components/shared/selects/AssignToUserSelect";
import type { ReassignCommunicationSalesAttributionModalData } from "@/lib/modals/data/modal-data-types";
import { useModalContext } from "@/lib/hooks/ui/useModalContext";
import { useCommunicationsHooks } from "@/lib/hooks/api/communications";

const MODAL_ID = MODAL_IDS.communications.reassignSalesAttribution;

/**
 * Lets employees move communication `user_id` (sales / activity attribution) to another teammate.
 */
export function ReassignCommunicationSalesAttributionModal(): React.ReactElement {
  const data = useModalContext<ReassignCommunicationSalesAttributionModalData>(MODAL_ID);
  const { closeModal } = useGlobalModalStack();
  const hooks = useCommunicationsHooks();
  const updateCommunication = hooks.useUpdate();

  const communicationId = data?.communicationId;

  const onAssign = async (assignedTo: string | null, _selectedKey: string) => {
    if (!communicationId) {
      throw new Error("Missing communication.");
    }
    if (assignedTo === null) {
      throw new Error("Select a teammate to attribute this communication.");
    }
    await updateCommunication.mutateAsync({
      id: communicationId,
      user_id: assignedTo,
    });
  };

  return (
    <UnifiedModal
      modalId={MODAL_ID}
      title="Set sales attribution"
      description="Credit a teammate as the communicator for dashboards, pipelines, and activity totals."
      size="md"
      forceModal
    >
      {communicationId ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This updates who is credited as the communicator in CRM reporting. It does not change when the interaction
            occurred.
          </p>
          <AssignToUserSelect
            currentUserId={data?.currentUserId}
            onAssign={onAssign}
            onSuccess={() => closeModal(MODAL_ID)}
            showUnassigned={false}
            showAllUsers
            placeholder="Choose teammate…"
            expandInline
            disabled={updateCommunication.isPending}
            getSuccessMessage={() => "Sales attribution updated"}
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Unable to load communication context.</p>
      )}
    </UnifiedModal>
  );
}
